import { Fragment, useMemo, useState } from "react";
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import SelectDropdownIcon from "../../components/SelectDropdownIcon";
import IntakeHeaderBackButton from "../../components/intake/IntakeHeaderBackButton";
import {
  PAGE_HEADER_TITLE_SX,
  STICKY_PAGE_HEADER_OUTER_ROW_SX,
  STICKY_PAGE_HEADER_TEXT_COLUMN_SX,
} from "../../components/layout/stickyPageHeaderTokens";
import { ReactComponent as SearchIcon } from "../../asserts/logo/Search.svg";
import {
  GATE2_DETAIL_TABLE_SX,
  GATE2_PAGINATION_FOCUS_SX,
  GATE2_TABLE_LINK_FOCUS_SX,
} from "./gate2ScopeTableStyles";
import Gate2TestCaseEvidenceExpandPanel from "./Gate2TestCaseEvidenceExpandPanel";
import { catalogIdFromMetricName, resolveCatalogMetricId } from "./implementedRagMetrics";

const DETAIL_TABLE_BORDER = "1px solid #E6E6E6";

const DETAIL_HEAD_SX = {
  fontFamily: "Lato, sans-serif",
  fontSize: 12,
  fontWeight: 600,
  lineHeight: "16px",
  letterSpacing: 0,
  color: "#555555",
  bgcolor: "#FFFFFF",
  height: 44,
  py: 0,
  px: 2,
  textAlign: "left",
  verticalAlign: "middle",
  whiteSpace: "nowrap",
  borderBottom: DETAIL_TABLE_BORDER,
  boxSizing: "border-box",
};

const DETAIL_BODY_SX = {
  fontFamily: "Lato, sans-serif",
  fontSize: 13,
  fontWeight: 400,
  lineHeight: "16px",
  letterSpacing: 0,
  color: "#383838",
  height: 56,
  py: 0,
  px: 2,
  verticalAlign: "middle",
  bgcolor: "#FFFFFF",
  borderBottom: DETAIL_TABLE_BORDER,
  boxSizing: "border-box",
};

const DETAIL_NAME_CELL_SX = {
  fontWeight: 600,
  color: "#0F172A",
};

const DETAIL_RISK_CELL_SX = {
  fontSize: 12,
  color: "#555555",
};

const DETAIL_VIEW_DETAILS_LINK_SX = {
  ...GATE2_TABLE_LINK_FOCUS_SX,
  fontFamily: "Lato, sans-serif",
  fontSize: 13,
  fontWeight: 700,
  lineHeight: "24px",
  letterSpacing: 0,
  color: "#005BF0",
  textUnderlineOffset: "2px",
  verticalAlign: "middle",
};

/** Same gap on Type (pr) and Actions (pl) so header/body stay aligned. */
const DETAIL_TYPE_ACTIONS_GAP = 3;

const DETAIL_TYPE_CELL_SX = {
  minWidth: 120,
  width: 120,
  pr: DETAIL_TYPE_ACTIONS_GAP,
  overflow: "visible",
  whiteSpace: "nowrap",
};

const DETAIL_ACTIONS_CELL_SX = {
  minWidth: 130,
  width: 130,
  pl: DETAIL_TYPE_ACTIONS_GAP,
  whiteSpace: "nowrap",
  textAlign: "left",
};

const DETAIL_ROWS_PER_PAGE_LABEL_SX = {
  fontFamily: "Lato, sans-serif",
  fontWeight: 400,
  fontSize: 14,
  lineHeight: "21px",
  letterSpacing: 0,
  color: "#555555",
  textAlign: "center",
};

const DETAIL_PAGINATION_TEXT_SX = {
  fontFamily: "Lato, sans-serif",
  fontWeight: 400,
  fontSize: 14,
  lineHeight: "21px",
  letterSpacing: 0,
  color: "#555555",
  textAlign: "center",
};

/** Standalone footer below the table — not part of the table border box (Figma). */
const DETAIL_PAGINATION_BAR_SX = {
  display: "flex",
  flexDirection: { xs: "column", sm: "row" },
  alignItems: { xs: "stretch", sm: "center" },
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: 1,
  flexShrink: 0,
  mt: 2,
  px: 0,
  py: 0,
  boxSizing: "border-box",
};

const DETAIL_TABLE_SX = GATE2_DETAIL_TABLE_SX;

/** Figma: Large / Default dropdown — 72×40, padding 8/12/8/10, border #BBBBBB, gradient fill. */
const DETAIL_ROWS_PER_PAGE_FILL =
  "linear-gradient(0deg, #FFFFFF, #FFFFFF), linear-gradient(180deg, rgba(255, 255, 255, 0.5) 0%, #F5F5F5 100%)";

const DETAIL_ROWS_PER_PAGE_SELECT_SX = {
  width: 72,
  minWidth: 72,
  maxWidth: 72,
  height: 40,
  flexShrink: 0,
  fontFamily: "Lato, sans-serif",
  fontSize: 16,
  lineHeight: "24px",
  color: "#555555",
  borderRadius: "4px",
  "& .MuiOutlinedInput-root": {
    height: 40,
    minHeight: 40,
    borderRadius: "4px",
    alignItems: "center",
    overflow: "hidden",
    bgcolor: "#FFFFFF",
    backgroundImage: DETAIL_ROWS_PER_PAGE_FILL,
    "& fieldset": {
      border: "1px solid #BBBBBB",
      borderRadius: "4px",
    },
    "&:hover fieldset": { border: "1px solid #BBBBBB" },
    "&.Mui-focused fieldset": {
      borderColor: "#005BF0 !important",
      borderWidth: "2px",
    },
  },
  "& .MuiSelect-select": {
    pt: "8px",
    pb: "8px",
    pl: "10px",
    pr: "12px !important",
    height: "40px !important",
    minHeight: "40px !important",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    fontSize: 16,
    lineHeight: "24px",
  },
  "& .MuiSelect-icon": {
    right: 8,
    top: "50%",
    marginTop: 0,
    transform: "translateY(-50%)",
    width: 24,
    height: 24,
    pointerEvents: "none",
  },
  "& .MuiSelect-iconOpen": {
    transform: "translateY(-50%) rotate(180deg)",
  },
};

const DETAIL_PAGINATION_CONTROL_SX = {
  ...GATE2_PAGINATION_FOCUS_SX,
  "& .MuiPaginationItem-root": {
    fontFamily: "Lato, sans-serif",
    fontSize: 13,
    minWidth: 32,
    height: 32,
  },
  "& .Mui-selected": {
    bgcolor: "#005BF0 !important",
    color: "#FFFFFF",
  },
  "& .Mui-selected.Mui-focusVisible": {
    boxShadow: "0 0 0 2px #FFFFFF, 0 0 0 4px #005BF0",
  },
};

const SEARCH_FIELD_SX = {
  width: { xs: "100%", sm: 400, md: 460 },
  maxWidth: "100%",
  flex: { xs: "1 1 100%", sm: "0 0 auto" },
  minWidth: 0,
  m: 0,
  "& .MuiOutlinedInput-root": {
    height: 32,
    minHeight: 32,
    borderRadius: "4px",
    bgcolor: "#FFFFFF",
    fontFamily: "Lato, sans-serif",
    fontSize: 16,
    lineHeight: "24px",
    color: "#555555",
    pl: "10px",
    pr: "10px",
    py: "4px",
    boxShadow: "inset 0px 2px 3px 0px rgba(85, 85, 85, 0.2)",
    "& fieldset": { border: "1px solid #707070" },
    "&:hover fieldset": { border: "1px solid #707070" },
    "&.Mui-focused fieldset": {
      borderColor: "#005BF0 !important",
      borderWidth: "2px",
    },
  },
};

function sourceBadgeSx(source) {
  if (source === "Custom") {
    return { bgcolor: "#FFEDD5", color: "#C2410C", border: "1px solid #C2410C" };
  }
  if (source === "Modified") {
    return { bgcolor: "#E6E6E6", color: "#555555", border: "1px solid #BBBBBB" };
  }
  return { bgcolor: "#F5EEFF", color: "#7F2CE8", border: "1px solid #9551EC" };
}

function sourceBadgeShape(source) {
  if (source === "Generated") return "12px";
  return "999px";
}

function sourceLabel(source) {
  if (source === "Custom" || source === "Defined") return "Custom";
  if (source === "Modified") return "Modified";
  return "Generated";
}

export function formatItemThreshold(item, scopeMode) {
  if (scopeMode !== "metric") return "—";
  if (item?.threshold == null || Number.isNaN(Number(item.threshold))) return "—";
  const n = Number(item.threshold);
  const dir = item.direction || "gte";
  const prefix = dir === "lte" ? "≤" : dir === "lt" ? "<" : dir === "eq" ? "=" : "≥";
  const suffix = item.unit === "percent" || item.unit === "%" ? "%" : "";
  return `${prefix} ${n}${suffix}`;
}

export function categoryLabel(item) {
  const fam = item?.families?.[0];
  if (!fam) return "—";
  const label = String(fam).replace(/_/g, " ");
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function formatCaseConfidence(score) {
  if (score == null || !Number.isFinite(Number(score))) return "—";
  const n = Number(score);
  if (n >= 0 && n <= 1) return n.toFixed(2);
  if (n > 1 && n <= 100) return (n / 100).toFixed(2);
  return n.toFixed(2);
}

function ResultOutlinePill({ passed, scored }) {
  if (!scored) {
    return (
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: 72,
          height: 26,
          px: 1.25,
          borderRadius: "13px",
          fontFamily: "Lato, sans-serif",
          fontSize: 12,
          fontWeight: 700,
          bgcolor: "#FFFFFF",
          color: "#B45309",
          border: "1px solid #FDE68A",
        }}
      >
        Not evaluated
      </Box>
    );
  }
  const pass = passed === true;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 72,
        height: 26,
        px: 1.25,
        borderRadius: "13px",
        fontFamily: "Lato, sans-serif",
        fontSize: 12,
        fontWeight: 700,
        bgcolor: "#FFFFFF",
        color: pass ? "#00754E" : "#B91C1C",
        border: `1px solid ${pass ? "#86EFAC" : "#FCA5A5"}`,
      }}
    >
      {pass ? "Passed" : "Failed"}
    </Box>
  );
}

function SourceTypeChip({ source }) {
  const label = sourceLabel(source);
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        fontWeight: 700,
        fontSize: label === "Generated" ? 13 : 12,
        height: 24,
        width: "fit-content",
        maxWidth: "none",
        flexShrink: 0,
        borderRadius: sourceBadgeShape(label),
        ...sourceBadgeSx(label),
        "& .MuiChip-label": {
          overflow: "visible",
          whiteSpace: "nowrap",
          px: label === "Generated" ? "8px" : label === "Custom" ? "12px" : "10px",
          py: 0,
          lineHeight: label === "Generated" ? "24px" : "normal",
        },
      }}
    />
  );
}

export default function ExecutionScopeItemDetailView({
  item,
  scopeMode,
  caseTableMode = "scope",
  testCasesById,
  /** Run snapshot (prompt, expected, answer, chunks) for evidence expand panels. */
  evidenceCasesById,
  onBack,
  onViewCaseDetails,
  showHeader = true,
}) {
  const evidenceResults = caseTableMode === "evidenceResults";
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const toggleExpanded = (id) => {
    const key = String(id);
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const resolveTestCase = (tc) => {
    const id = tc?.id;
    const key = id != null ? String(id) : "";
    if (evidenceResults && evidenceCasesById) {
      const exec =
        evidenceCasesById.get?.(id) ||
        evidenceCasesById.get?.(key);
      if (exec) return exec;
    }
    return (
      testCasesById?.get?.(id) ||
      testCasesById?.get?.(key) ||
      {}
    );
  };

  const evidenceRowFromCase = (tc) => ({
    testCaseId: tc.id,
    score: tc.score,
    passed: tc.passed,
    reason: tc.reason,
  });

  const cases = useMemo(() => item?.cases || [], [item]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return cases;
    return cases.filter((tc) => {
      const name = (tc.name || "").toLowerCase();
      if (evidenceResults) return name.includes(q);
      const risk = (tc.riskName || "").toLowerCase();
      const src = sourceLabel(tc.source).toLowerCase();
      return name.includes(q) || risk.includes(q) || src.includes(q);
    });
  }, [cases, search, evidenceResults]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const safePage = Math.min(page, pageCount);
  const pageStart = (safePage - 1) * rowsPerPage;
  const pageRows = filtered.slice(pageStart, pageStart + rowsPerPage);
  const rangeFrom = filtered.length ? pageStart + 1 : 0;
  const rangeTo = filtered.length ? Math.min(pageStart + rowsPerPage, filtered.length) : 0;

  const title = item?.name || "Metric";
  const catalogMetricId =
    resolveCatalogMetricId(item) ||
    catalogIdFromMetricName(item?.name) ||
    String(item?.cases?.[0]?.catalogMetricId || item?.cases?.[0]?.metric_id || "").trim();

  const paginationBar = (
    <Box sx={DETAIL_PAGINATION_BAR_SX}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
        <Typography sx={DETAIL_ROWS_PER_PAGE_LABEL_SX}>Rows per page</Typography>
        <Select
          value={rowsPerPage}
          onChange={(e) => {
            setRowsPerPage(Number(e.target.value));
            setPage(1);
          }}
          IconComponent={SelectDropdownIcon}
          sx={DETAIL_ROWS_PER_PAGE_SELECT_SX}
        >
          {[5, 10, 20, 50].map((n) => (
            <MenuItem key={n} value={n}>
              {n}
            </MenuItem>
          ))}
        </Select>
        <Typography sx={DETAIL_PAGINATION_TEXT_SX}>
          {filtered.length ? `${rangeFrom}-${rangeTo} of ${filtered.length}` : "0-0 of 0"}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          justifyContent: { xs: "flex-start", sm: "flex-end" },
        }}
      >
        <Pagination
          count={pageCount}
          page={safePage}
          onChange={(_, p) => setPage(p)}
          shape="rounded"
          sx={DETAIL_PAGINATION_CONTROL_SX}
        />
      </Box>
    </Box>
  );

  return (
    <Box sx={{ px: 3, pt: 2, pb: 3, bgcolor: "#FFFFFF", minHeight: "100%", boxSizing: "border-box" }}>
      {showHeader ? (
      <Box sx={{ ...STICKY_PAGE_HEADER_OUTER_ROW_SX, mb: 2 }}>
        <IntakeHeaderBackButton
          onClick={onBack}
          aria-label={evidenceResults ? "Back to quality scorecard" : "Back to execution scope"}
        />
        <Box sx={STICKY_PAGE_HEADER_TEXT_COLUMN_SX}>
          <Typography component="h1" sx={PAGE_HEADER_TITLE_SX}>
            {title}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "24px",
              color: "#383838",
            }}
          >
        <Typography
          component="span"
          sx={{
            fontFamily: "Lato, sans-serif",
            fontSize: 14,
            lineHeight: "100%",
            letterSpacing: 0,
            color: "#383838",
            whiteSpace: "nowrap",
          }}
        >
          <Box component="span" sx={{ fontWeight: 700 }}>
            {cases.length}
          </Box>
          <Box component="span" sx={{ fontWeight: 400 }}>
            {` Test case${cases.length === 1 ? "" : "s"}`}
          </Box>
        </Typography>
        <Typography
          component="span"
          sx={{
            fontFamily: "Lato, sans-serif",
            fontSize: 12,
            lineHeight: "100%",
            letterSpacing: 0,
            color: "#383838",
            whiteSpace: "nowrap",
          }}
        >
          <Box component="span" sx={{ fontWeight: 700 }}>Category : </Box>
          <Box component="span" sx={{ fontWeight: 400 }}>{categoryLabel(item)}</Box>
        </Typography>
        <Typography
          component="span"
          sx={{
            fontFamily: "Lato, sans-serif",
            fontSize: 12,
            lineHeight: "100%",
            letterSpacing: 0,
            color: "#383838",
            whiteSpace: "nowrap",
          }}
        >
          <Box component="span" sx={{ fontWeight: 700 }}>Threshold : </Box>
          <Box component="span" sx={{ fontWeight: 400 }}>{formatItemThreshold(item, scopeMode)}</Box>
        </Typography>
          </Box>
        </Box>
      </Box>
      ) : null}

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          gap: 1.5,
          mb: 2,
        }}
      >
        <TextField
          margin="none"
          placeholder="Search test cases..."
          inputProps={{ "aria-label": "Search test cases" }}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ margin: 0, maxHeight: "unset" }}>
                <SearchIcon style={{ width: 24, height: 24, display: "block" }} />
              </InputAdornment>
            ),
          }}
          sx={SEARCH_FIELD_SX}
        />
      </Box>

      <TableContainer sx={{ border: DETAIL_TABLE_BORDER, borderRadius: "8px", overflow: "hidden" }}>
          <Table size="small" sx={DETAIL_TABLE_SX} aria-label={`Test cases for ${title}`}>
            {evidenceResults ? (
              <colgroup>
                <col style={{ width: 40 }} />
                <col style={{ width: "52%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: 140 }} />
              </colgroup>
            ) : (
              <colgroup>
                <col style={{ width: "42%" }} />
                <col style={{ width: "22%" }} />
                <col style={{ width: 120 }} />
                <col style={{ width: 130 }} />
              </colgroup>
            )}
            <TableHead>
              <TableRow>
                {evidenceResults ? (
                  <TableCell sx={{ ...DETAIL_HEAD_SX, width: 40, px: 1 }} aria-hidden />
                ) : null}
                <TableCell sx={DETAIL_HEAD_SX}>Test Case Name</TableCell>
                {evidenceResults ? (
                  <>
                    <TableCell sx={DETAIL_HEAD_SX}>Confidence</TableCell>
                    <TableCell sx={DETAIL_HEAD_SX}>Result</TableCell>
                  </>
                ) : (
                  <>
                    <TableCell sx={DETAIL_HEAD_SX}>Risk</TableCell>
                    <TableCell sx={{ ...DETAIL_HEAD_SX, ...DETAIL_TYPE_CELL_SX }}>Type</TableCell>
                    <TableCell sx={{ ...DETAIL_HEAD_SX, ...DETAIL_ACTIONS_CELL_SX }}>Actions</TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.length ? (
                pageRows.map((tc) => {
                  const scored = tc.score != null;
                  const rowKey = String(tc.id);
                  const expanded = evidenceResults && expandedIds.has(rowKey);
                  const mainRow = (
                    <TableRow hover={false} sx={evidenceResults ? { height: 56 } : undefined}>
                      {evidenceResults ? (
                        <TableCell sx={{ ...DETAIL_BODY_SX, width: 40, px: 0.5 }}>
                          <IconButton
                            size="small"
                            aria-expanded={expanded}
                            aria-label={expanded ? "Collapse test case" : "Expand test case"}
                            onClick={() => toggleExpanded(tc.id)}
                            sx={{
                              color: "#555555",
                              transform: expanded ? "rotate(90deg)" : "none",
                              transition: "transform 0.15s ease",
                            }}
                          >
                            <KeyboardArrowRightIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      ) : null}
                      <TableCell sx={{ ...DETAIL_BODY_SX, ...DETAIL_NAME_CELL_SX, maxWidth: 0 }}>
                        {evidenceResults ? (
                          <Link
                            component="button"
                            type="button"
                            underline="hover"
                            onClick={() => toggleExpanded(tc.id)}
                            sx={{
                              ...DETAIL_VIEW_DETAILS_LINK_SX,
                              fontWeight: 600,
                              display: "block",
                              maxWidth: "100%",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              textAlign: "left",
                            }}
                            title={tc.name || undefined}
                          >
                            {tc.name || "—"}
                          </Link>
                        ) : (
                          tc.name || "—"
                        )}
                      </TableCell>
                      {evidenceResults ? (
                        <>
                          <TableCell sx={DETAIL_BODY_SX}>{formatCaseConfidence(tc.score)}</TableCell>
                          <TableCell sx={DETAIL_BODY_SX}>
                            <ResultOutlinePill passed={tc.passed} scored={scored} />
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell sx={{ ...DETAIL_BODY_SX, ...DETAIL_RISK_CELL_SX }}>
                            {tc.riskName || "—"}
                          </TableCell>
                          <TableCell sx={{ ...DETAIL_BODY_SX, ...DETAIL_TYPE_CELL_SX }}>
                            <SourceTypeChip source={tc.source} />
                          </TableCell>
                          <TableCell sx={{ ...DETAIL_BODY_SX, ...DETAIL_ACTIONS_CELL_SX }}>
                            <Link
                              component="button"
                              type="button"
                              underline="hover"
                              onClick={() => onViewCaseDetails?.(tc.id)}
                              sx={DETAIL_VIEW_DETAILS_LINK_SX}
                            >
                              View Details
                            </Link>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  );

                  if (!evidenceResults) {
                    return <Fragment key={rowKey}>{mainRow}</Fragment>;
                  }

                  return (
                    <Fragment key={rowKey}>
                      {mainRow}
                      <TableRow>
                        <TableCell colSpan={4} sx={{ py: 0, px: 0, borderBottom: DETAIL_TABLE_BORDER }}>
                          <Collapse in={expanded} timeout="auto" unmountOnExit>
                            <Gate2TestCaseEvidenceExpandPanel
                              testCase={resolveTestCase(tc)}
                              row={evidenceRowFromCase(tc)}
                              catalogMetricId={catalogMetricId}
                            />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={evidenceResults ? 4 : 4}
                    sx={{ ...DETAIL_BODY_SX, textAlign: "center", color: "#64748B" }}
                  >
                    No test cases match your search.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

      {paginationBar}
    </Box>
  );
}
